import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Trophy, Star, BookOpen, CalendarCheck, Award, Medal, Lock } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const ICON_MAP = {
  trophy: Trophy, star: Star, book: BookOpen,
  'calendar-check': CalendarCheck, award: Award, medal: Medal,
};

export default function BadgeGallery({ child }) {
  const [badges, setBadges] = useState([]);
  const [progress, setProgress] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!child?.id) return;
    (async () => {
      try {
        const [catalog, prog] = await Promise.all([
          api.get('/api/badges'),
          api.get(`/api/badges/progress/${child.id}`),
        ]);
        setBadges(catalog.data);
        setProgress(prog.data);
      } catch { toast.error('Failed to load badges'); }
      finally { setLoading(false); }
    })();
  }, [child?.id]);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          className="w-10 h-10 border-4 border-[#38BDF8] border-t-transparent rounded-full" />
      </div>
    );
  }

  const progressById = Object.fromEntries(progress.map((p) => [p.id, p]));
  const earned = new Set(child?.badges || []);
  const earnedCount = badges.filter((b) => earned.has(b.id)).length;
  const pct = badges.length > 0 ? Math.round((earnedCount / badges.length) * 100) : 0;

  return (
    <div data-testid="badge-gallery">
      {/* Header */}
      <div className="bg-white rounded-[2rem] p-6 sm:p-8 mb-6 shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-slate-100/50">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="font-nunito text-xs font-bold uppercase tracking-wider text-[#FD5E53] mb-1.5">
              Achievement Collection
            </p>
            <h2 className="font-fredoka text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight">
              {child.name}&apos;s Badges
            </h2>
            <p className="font-nunito text-slate-500 mt-1">
              {earnedCount} of {badges.length} badges earned
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative w-20 h-20">
              <svg className="w-full h-full -rotate-90">
                <circle cx="40" cy="40" r="34" stroke="currentColor" strokeWidth="6" fill="none" className="text-slate-100" />
                <motion.circle
                  cx="40" cy="40" r="34"
                  stroke="currentColor" strokeWidth="6" fill="none" strokeLinecap="round"
                  className="text-[#FD5E53]"
                  initial={{ strokeDasharray: `0 ${2 * Math.PI * 34}` }}
                  animate={{ strokeDasharray: `${(pct / 100) * 2 * Math.PI * 34} ${2 * Math.PI * 34}` }}
                  transition={{ duration: 1.2, ease: 'easeOut' }}
                />
              </svg>
              <div className="absolute inset-0 flex items-center justify-center font-fredoka font-semibold text-lg text-slate-800">
                {pct}%
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
        {badges.map((badge, i) => {
          const isEarned = earned.has(badge.id);
          const Icon = ICON_MAP[badge.icon] || Trophy;
          const p = progressById[badge.id];
          return (
            <motion.div
              key={badge.id}
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.05 }}
              whileHover={{ y: -4 }}
              className={`relative p-5 sm:p-6 rounded-[1.75rem] border transition-all ${
                isEarned
                  ? 'bg-white shadow-[0_8px_30px_rgb(0,0,0,0.04)] border-slate-100/50 hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)]'
                  : 'bg-slate-50/60 border-slate-100'
              }`}
              data-testid={`badge-${badge.id}`}
            >
              <div className="flex items-start gap-4">
                <div
                  className={`relative w-16 h-16 sm:w-20 sm:h-20 rounded-full flex items-center justify-center flex-shrink-0 ${!isEarned ? 'grayscale opacity-80' : ''}`}
                  style={{ backgroundColor: isEarned ? `${badge.color}22` : '#f1f5f9' }}
                >
                  <Icon className="w-8 h-8 sm:w-10 sm:h-10" style={{ color: badge.color }} strokeWidth={1.8} />
                  {!isEarned && (
                    <div className="absolute inset-0 flex items-center justify-center bg-slate-900/30 rounded-full">
                      <Lock className="w-4 h-4 text-white" />
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <h3 className="font-fredoka text-base font-semibold text-slate-800">{badge.name}</h3>
                  <p className="font-nunito text-xs text-slate-500 leading-snug mt-0.5">{badge.description}</p>
                  {isEarned ? (
                    <div className="mt-2 inline-flex items-center gap-1 bg-[#DCFCE7] text-[#16A34A] px-2.5 py-0.5 rounded-full text-[11px] font-fredoka font-medium">
                      ✓ Earned
                    </div>
                  ) : p ? (
                    <div className="mt-3" data-testid={`badge-progress-${badge.id}`}>
                      <div className="flex items-baseline justify-between mb-1">
                        <span className="font-nunito text-[11px] font-semibold text-slate-600">
                          {p.current} / {p.target} {p.unit}
                        </span>
                        <span className="font-fredoka text-xs font-semibold text-[#FD5E53]">{Math.round(p.percent)}%</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <motion.div
                          initial={{ width: 0 }}
                          animate={{ width: `${p.percent}%` }}
                          transition={{ duration: 1, delay: 0.2 + i * 0.05 }}
                          className="h-full rounded-full"
                          style={{ backgroundColor: badge.color }}
                        />
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
